import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout, DocsCard, DocsCardGrid } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

export default function AtlasDocs() {
  return (
    <>
      <NextSeo
        title="Atlas owner guide | Chat.co"
        description="Access, approvals, knowledge quality and recovery in Atlas."
      />
      <DocsArticleLayout
        category="Atlas"
        categoryColor="text-blue-700"
        title="Atlas owner guide"
        description="Use Atlas with clear expectations about access, actions and evidence."
        readingTime="3 min read"
        tableOfContents={[
          { id: 'availability', title: 'What is available' },
          { id: 'guides', title: 'Choose a guide' },
          { id: 'before-action', title: 'Before an action' },
        ]}
        nextArticle={{
          title: 'Getting started with Atlas',
          href: PageLink.docsAtlasGettingStarted,
        }}>
        <section id="availability">
          <h2>What is available</h2>
          <DocsCallout title="Access and capabilities are separate">
            Atlas is available to enrolled accounts with the required permissions. Enrollment does
            not enable every capability. A workflow described here may still be unavailable in your
            account.
          </DocsCallout>
          <p>
            Use the controls and status shown in your workspace to check availability. Automated
            onboarding, knowledge refresh, recurring routines and connected integrations can require
            separate enablement. A suggested plan or a connection card does not prove that a
            workflow is ready to run.
          </p>
          <p>
            If a capability is unavailable, continue in Classic where you have access, or{' '}
            <Link href={PageLink.contactUs}>contact support about availability</Link>. These guides
            do not announce a rollout date or a change to your plan.
          </p>
        </section>
        <section id="guides">
          <h2>Choose a guide</h2>
          <DocsCardGrid>
            <DocsCard title="Getting started with Atlas" href={PageLink.docsAtlasGettingStarted}>
              Access, your first request and returning to Classic.
            </DocsCard>
            <DocsCard title="Review and Apply in Atlas" href={PageLink.docsAtlasApprovals}>
              Exact effects, approval buttons, declining proposals and expiry.
            </DocsCard>
            <DocsCard title="Atlas knowledge and answer quality" href={PageLink.docsAtlasKnowledge}>
              Sources, refresh and meaningful test evidence.
            </DocsCard>
            <DocsCard
              title="Atlas roles, privacy and key delivery"
              href={PageLink.docsAtlasPrivacy}>
              Permissions and handling a private key delivery.
            </DocsCard>
            <DocsCard title="Troubleshooting Atlas" href={PageLink.docsAtlasTroubleshooting}>
              Interruptions, unknown outcomes and limits.
            </DocsCard>
          </DocsCardGrid>
        </section>
        <section id="before-action">
          <h2>Before an action</h2>
          <p>
            Each consequential action needs its own review card and an explicit click on its
            approval button. The button may say Approve, Approve &amp; send, Approve &amp; publish,
            Approve &amp; delete or Apply. An ordinary chat yes is not approval. Informational plans
            have no blanket Apply: review each proposed effect when its own card appears.
          </p>
          <p>
            A submitted request is not a confirmed result. Check the task and evidence before
            assuming a change or purchase completed. Follow the{' '}
            <Link href={PageLink.docsAtlasTroubleshooting}>unknown outcome guidance</Link> if
            confirmation is missing.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
